// src/layouts/SidebarLayout/index.tsx
import React, { ReactNode } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { SidebarProvider, useSidebar } from '../../contexts/SidebarContext';
import { SidebarLink, LayoutConfig } from './types';
import Header from './Header';
import Sidebar from './Sidebar';
import TabBar from '../../components/TabBar';

export interface SidebarLayoutProps {
  links: SidebarLink[];
  user: {
    name: string;
    email: string;
    role: string;
    avatar?: string;
  };
  config?: Partial<LayoutConfig>;
  children?: ReactNode;
}

const defaultConfig: LayoutConfig = {
  sidebarWidth: { collapsed: 72, expanded: 280 },
  headerHeight: 64,
  showTabs: true,
  showUserInfo: true,
  allowSidebarToggle: true,
};

const SidebarLayoutContent: React.FC<SidebarLayoutProps & { finalConfig: LayoutConfig }> = ({
  links,
  user,
  finalConfig,
  children
}) => {
  const theme = useTheme();
  const { getWidth, isMobile } = useSidebar();

  // getWidth() should already reflect responsive + collapsed state from the context.
  const sidebarWidth = getWidth();

  // Main area only shifts on desktop; stays full width on mobile.
  const mainMarginLeft = isMobile ? 0 : sidebarWidth;
  const mainCalcWidth = isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`;

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        position: 'relative',
        '--sidebar-w': `${sidebarWidth}px` as any,
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: theme.zIndex.drawer,
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
          width: isMobile ? 0 : sidebarWidth, // Hide the rail on mobile; Drawer will handle UI
          pointerEvents: isMobile ? 'none' : 'auto',
        }}
      >
        <Sidebar links={links} config={finalConfig} />
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: 'background.default',

          marginLeft: `${mainMarginLeft}px`,
          width: mainCalcWidth,
          maxWidth: mainCalcWidth,

          transition: theme.transitions.create(['margin-left', 'width', 'max-width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        {/* Header + optional TabBar */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar,
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              height: finalConfig.headerHeight,
              minHeight: finalConfig.headerHeight,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Header user={user} config={finalConfig} />
          </Box>
          {finalConfig.showTabs && <TabBar />}
        </Box>

        <Box
          sx={{
            flex: 1,
            padding: theme.spacing(3),
            overflow: 'auto',
            minHeight: 0,
            maxWidth: '100%',
          }}
        >
          {children ?? <Outlet />}
        </Box>
      </Box>
    </Box>
  );
};

const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  links,
  user,
  config = {},
  children
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const finalConfig: LayoutConfig = { ...defaultConfig, ...config };

  return (
    <SidebarProvider
      initialCollapsed={false}
      widthConfig={finalConfig.sidebarWidth}
    >
      <SidebarLayoutContent
        links={links}
        user={user}
        finalConfig={finalConfig}
      >
        {children}
      </SidebarLayoutContent>
    </SidebarProvider>
  );
};

export default SidebarLayout;